import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "./Mask";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/menu", label: "Menu" },
  { to: "/franchise", label: "Franchise" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,box-shadow,padding] duration-300 ${
        scrolled ? "py-2 bg-koreyaah-cream/85 backdrop-blur-xl border-b border-koreyaah-indigo/10" : "py-4 bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="Koreyaah home">
          <Wordmark />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-semibold uppercase tracking-wide rounded-full transition-colors duration-200 ${
                  isActive ? "text-koreyaah-red" : "text-koreyaah-indigo hover:text-koreyaah-red"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/franchise"
            data-testid="nav-cta-franchise"
            className="ml-3 rounded-full bg-koreyaah-red px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-koreyaah-red-deep"
          >
            Own a Franchise
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden grid h-11 w-11 place-items-center rounded-full bg-koreyaah-indigo text-white"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-koreyaah-cream/95 backdrop-blur-xl border-t border-koreyaah-indigo/10"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-2xl text-base font-semibold uppercase tracking-wide ${
                      isActive ? "bg-koreyaah-red text-white" : "text-koreyaah-indigo"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
